import { ReactElement, useEffect, useState, ChangeEvent } from "react";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import { IVariableEntity, IEditionResponse, IAxisEntity } from "../../types";
import {
  Card,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip
} from "@mui/material";
import { cloneObject } from "../../../../system/utils";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { addAlert } from "../../../../actions";
import { useDispatch, useSelector } from "react-redux";
import Filter from "./filter";
import { ISelectedNode, IState } from "../../../../types";
import Select from "@mui/material/Select";
import { getMachinesByNode } from "../../../interruptions/dashboard/utils";
import ColorPicker from "../../../../components/colorPicker";

const VariablesConfig = (): ReactElement => {
  const dispatch = useDispatch<any>();
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [filter, setFilter] = useState<string>("");
  const [selectedVariable, setSelectedVariable] = useState<IVariableEntity | null>(null);
  const [variables, setVariables] = useState<IVariableEntity[]>([]);
  const [execCommand] = useAsyncFetch<IEditionResponse>();
  const [machines] = useState<ISelectedNode[]>(getMachinesByNode(selectedNode));
  const { data } = useFetch<IVariableEntity[]>(
    {
      module: "acquisition",
      query: "getVariableEntitiesByZone",
      payload: {
        zoneID: selectedNode.nodeID
      }
    });
  const { data: axes } = useFetch<IAxisEntity[]>(
    {
      module: "acquisition",
      query: "getAxesEntities",
      payload: {}
    });

  useEffect(() => {
    setVariables((data ?? []).sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
  }, [data]);

  const gridStyle = {
    gridTemplateColumns: "400px 400px",
    gridTemplateRows: "1fr",
    justifyItems: "stretch",
    alignItems: "stretch"
  };

  const handleChange = (field: keyof IVariableEntity, value: any) => {
    if (selectedVariable) {
      setSelectedVariable({ ...selectedVariable, [field]: value });
    }
  };

  const onResetClick = () => {
    if (!selectedVariable) {
      return;
    }

    if (!selectedVariable.variableID) {
      setSelectedVariable(null);
    } else {
      const found = variables?.find(x => x.variableID === selectedVariable.variableID);
      if (found) {
        setSelectedVariable(cloneObject(found));
      }
    }
  };

  const onSaveClick = () => {
    if (selectedVariable) {
      execCommand({
        module: "acquisition",
        query: "insertOrUpdateEntities",
        payload: {
          entity: "VARIABLE",
          action: selectedVariable.variableID ? "UPDATE" : "INSERT",
          data: {
            ...selectedVariable
          }
        }
      }).then(response => {
        if (response && response.result) {
          if (!selectedVariable.variableID) {
            const newItem = { ...selectedVariable, variableID: response.editedID };
            setVariables([...variables, newItem].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
            setSelectedVariable(newItem);

          } else {
            const found = variables?.find(x => x.variableID === selectedVariable.variableID);
            if (found) {
              found.name = selectedVariable.name;
              found.axisID = selectedVariable.axisID;
              found.color = selectedVariable.color ?? (selectedVariable.color === "" ? null : selectedVariable.color);
              found.minimum = selectedVariable.axisID ? null : selectedVariable.minimum;
              found.maximum = selectedVariable.axisID ? null : selectedVariable.maximum;
              found.unit = selectedVariable.axisID ? null : selectedVariable.unit;

              setVariables([...variables].sort((a, b) => a.name > b.name ? 1 : a.name < b.name ?  -1 : 0));
            }
          }

          dispatch(addAlert({ message: "The axis was saved!", severity: "success", hash: undefined }));
        } else {
          dispatch(addAlert({ message: response?.message ?? "ERROR", severity: "error", hash: undefined }));
        }
      });
    }
  };

  const selectedAxis = selectedVariable && axes && selectedVariable.axisID ? axes.find(x => x.axisID === selectedVariable.axisID) ?? null : null;
  const selectedMachine = selectedVariable && machines ? machines.find(x => x.nodeID === selectedVariable.machineID) : null;

  return (
    <div className="display-flex-row justify-content-center">
      <div className="display-grid" style={gridStyle}>
        <Card className="m-1">
          <CardHeader title="Variables"/>
          <CardContent>
            <div className="display-grid" style={{ gridTemplateRows: "auto auto" }}>
              <Filter filter={filter} setFilter={setFilter} />
              <div style={{ overflowY: "auto", marginTop: ".5em", height: "600px", maxHeight: "600px" }}>
                <List>
                  {variables && variables.filter(x => filter === "" || x.name.toLowerCase().includes(filter)).map(variable => {

                    return (
                      <ListItemButton key={`variable-${variable.variableID}`}
                        selected={selectedVariable !== null && selectedVariable.variableID === variable.variableID}
                        onClick={() => setSelectedVariable(cloneObject(variable))}>
                        <ListItemText primary={variable.name}/>
                      </ListItemButton>
                    );
                  })}
                </List>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="m-1">
          <CardHeader title="Detail"
            action={
              <>
                <Tooltip title="Save">
                  <span>
                    <IconButton aria-label="save" color="success" onClick={onSaveClick}
                      disabled={selectedVariable === null}>
                      <SaveIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reset">
                  <span>
                    <IconButton aria-label="cancel" onClick={onResetClick} disabled={selectedVariable === null}>
                      <RefreshIcon/>
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            }/>
          <CardContent>
            <div style={{ overflowY: "auto", height: "600px" }}>
              {selectedVariable &&
                <div className="display-flex-column">
                  <TextField
                    className="m-1"
                    variant="standard"
                    label={"Code"}
                    value={selectedVariable.code}
                    disabled={true}/>

                  <TextField
                    className="m-1"
                    variant="standard"
                    label={"Machine"}
                    value={selectedMachine ? selectedMachine.name : "---"}
                    disabled={true}/>

                  <TextField
                    className="m-1"
                    variant="standard"
                    placeholder={"Name"}
                    label={"Name"}
                    value={selectedVariable.name}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("name", event.target.value)}/>

                  <FormControl className="m-1" variant="standard">
                    <InputLabel id="demo-simple-select-standard-label">Axis</InputLabel>
                    <Select
                      variant="standard"
                      value={axes && axes.length > 0 ? `${selectedVariable.axisID ?? 0}` : "0"}
                      onChange={(event) => handleChange("axisID", event.target.value === "0" ? null : parseInt(event.target.value as string, 10))}>
                      <MenuItem value={"0"}>NONE</MenuItem>
                      { axes && axes.map(axis => {

                        return (
                          <MenuItem key={`variable-axis-${axis.axisID}`} value={`${axis.axisID}`}>
                            <div className="display-flex-row w-100">
                              <p className="flex-grow-1">{`${axis.name} ${axis.unit && axis.unit !== "" ? `[${axis.unit}]` : ""}`}</p>
                              <p>{`[${axis.minimum}, ${axis.maximum}]`}</p>
                            </div>
                          </MenuItem>
                        );
                      })
                      }
                    </Select>
                  </FormControl>

                  <TextField
                    className="m-1"
                    variant="standard"
                    placeholder={"Unit"}
                    label={"Unit"}
                    value={(selectedAxis ? selectedAxis.unit : selectedVariable.unit) ?? ""}
                    disabled={selectedVariable.axisID !== null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("unit", event.target.value)}/>

                  <TextField
                    className="m-1"
                    label="Minimum"
                    variant="standard"
                    size="small"
                    value={(selectedAxis ? selectedAxis.minimum : selectedVariable.minimum) ?? 0}
                    type="number"
                    disabled={selectedVariable.axisID !== null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("minimum", parseFloat(event.target.value))}
                  />

                  <TextField
                    className="m-1"
                    label="Maximum"
                    variant="standard"
                    size="small"
                    value={(selectedAxis ? selectedAxis.maximum : selectedVariable.maximum) ?? 100}
                    type="number"
                    disabled={selectedVariable.axisID !== null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange ("maximum", parseFloat(event.target.value))}
                  />

                  <div
                    className="m-1 flex-grow-color-picker"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      borderBottom: "1px solid rgba(255, 255, 255, .7)"
                    }}>
                    <InputLabel style={{ transform: "translate(0px, 6px) scale(0.75)" }}>Color</InputLabel>
                    <div className="display-grid" style={{ gridTemplateColumns: "1fr auto" }}>
                      <ColorPicker color={selectedVariable.color ?? "#4344F0"}
                        onChange={(newColor) => handleChange("color", newColor)} disableAlpha={true}/>
                    </div>
                  </div>
                </div>
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VariablesConfig;
